import config from '../config';
import { getToken } from './auth';

const API_BASE_URL = `${config.api.baseURL}/api/v1`;

/**
 * Run a comparison between two models
 * Returns a ReadableStream for Server-Sent Events
 */
export const compareModels = async (projectId, comparisonName, inputValues, config1, config2, workspaceId = null) => {
  const token = getToken();
  
  const requestBody = {
    project_id: projectId,
    comparison_name: comparisonName,
    input_values: inputValues,
    llm_config_1: config1,
    llm_config_2: config2
  };
  if (workspaceId) {
    requestBody.workspace_id = workspaceId;
  }
  
  const response = await fetch(`${API_BASE_URL}/compare-models`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(requestBody)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to start comparison: ${response.statusText}`);
  }

  return response.body.getReader();
};

/**
 * Parse SSE stream from comparison
 */
export const parseComparisonStream = async (reader, callbacks) => {
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            
            if (data.type === 'status' && callbacks.onStatus) {
              callbacks.onStatus(data);
            } else if (data.type === 'chunk' && callbacks.onChunk) {
              callbacks.onChunk(data);
            } else if (data.type === 'model_complete' && callbacks.onModelComplete) {
              callbacks.onModelComplete(data);
            } else if (data.type === 'done' && callbacks.onComplete) {
              callbacks.onComplete(data);
            } else if (data.type === 'error' && callbacks.onError) {
              callbacks.onError(data);
            }
          } catch (e) {
            console.error('Failed to parse SSE data:', e);
          }
        }
      }
    }
  } catch (error) {
    if (callbacks.onError) {
      callbacks.onError({ message: error.message });
    }
  }
};

/**
 * Get comparisons (by workspace or project)
 */
export const getComparisons = async ({ workspaceId, projectId, limit = 100 }) => {
  const token = getToken();
  
  const body = { limit };
  if (workspaceId) body.workspace_id = workspaceId;
  if (projectId) body.project_id = projectId;
  
  const response = await fetch(`${API_BASE_URL}/comparisons`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    throw new Error('Failed to fetch comparisons');
  }

  return response.json();
};

/**
 * Get detailed comparison results
 */
export const getComparisonDetail = async (comparisonId) => {
  const token = getToken();
  
  const response = await fetch(`${API_BASE_URL}/comparisons/detail`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      comparison_id: comparisonId
    })
  });

  if (!response.ok) {
    throw new Error('Failed to fetch comparison detail');
  }

  return response.json();
};

/**
 * Delete a comparison
 */
export const deleteComparison = async (comparisonId) => {
  const token = getToken();
  
  const response = await fetch(`${API_BASE_URL}/comparisons/delete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      comparison_id: comparisonId
    })
  });

  if (!response.ok) {
    throw new Error('Failed to delete comparison');
  }

  return response.json();
};

